import { useState } from 'react'
import { GrimoireTabs } from '@arcanecircle/ui/grimoire/tabs'

export interface Tab {
  id: string
  label: string
  /** Optional count pip rendered on the right edge (e.g. unread, queue size). */
  pip?: number | string
  /** Optional leading icon. */
  icon?: React.ReactNode
  content: React.ReactNode
}

interface TabPanelProps {
  tabs: Tab[]
  defaultTab?: string
}

/**
 * v5 sidebar tab strip. Wraps `<GrimoireTabs variant="underline" />` so the
 * sidebar tab chrome is the same primitive Mike's other surfaces use —
 * mint underline on active, color transition on the duration token.
 *
 * Layout chrome (`vtt-sidebar-tabs` / `vtt-sidebar-content` wrappers) still
 * lives in vtt-shell.css; this component just stuffs icon + label + pip into
 * the GrimoireTabs `label` ReactNode and renders the active tab's content.
 */
export function TabPanel({ tabs, defaultTab }: TabPanelProps) {
  const [activeTab, setActiveTab] = useState(defaultTab || tabs[0]?.id || '')
  const activeTabContent = tabs.find((tab) => tab.id === activeTab)?.content

  return (
    <>
      <nav className="vtt-sidebar-tabs" role="tablist">
        <GrimoireTabs
          variant="underline"
          value={activeTab}
          onValueChange={setActiveTab}
          tabs={tabs.map((tab) => ({
            value: tab.id,
            label: (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem' }}>
                {tab.icon}
                {tab.label}
                {tab.pip != null && <span className="pip">{tab.pip}</span>}
              </span>
            ),
          }))}
        />
      </nav>
      <div className="vtt-sidebar-content" role="tabpanel">
        {activeTabContent}
      </div>
    </>
  )
}
